import { useEffect, useRef, useState, type ReactNode } from 'react';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import { auth } from '../lib/api';
import { portal } from '../lib/portal';
import { useRequiredAgreements } from '../hooks/usePortal';
import { classifySaveError } from '../lib/steps';
import InlineError from './InlineError';

interface TermsGateModalProps {
  children: ReactNode;
}

/**
 * First-login blocking agreement gate. Wraps the authenticated app tree and
 * always renders `children`; when the signed-in applicant has an outstanding
 * `portal_access` agreement it overlays a non-dismissable modal on top —
 * dropping to nothing (no route change, no reload) once every agreement is
 * accepted.
 */
export default function TermsGateModal({ children }: TermsGateModalProps) {
  const { data, loading, reload } = useRequiredAgreements();
  const current = data?.agreements[0];

  const [checked, setChecked] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Reset the checkbox/error whenever a different agreement (or a stale-
  // version refresh of the same one) is served.
  useEffect(() => {
    setChecked(false);
    setTypedName('');
    setError(undefined);
  }, [current?.agreementId, current?.versionId]);

  useEffect(() => {
    if (!current) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';

    const focusable = () => {
      const container = dialogRef.current;
      if (!container) return [] as HTMLElement[];
      return Array.from(
        container.querySelectorAll<HTMLElement>('button, input, [href], [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => !el.hasAttribute('disabled'));
    };
    focusable()[0]?.focus();

    // Deliberately no Escape handler and no outside-click-closes — this modal
    // cannot be dismissed. Only Tab is intercepted, to keep focus inside it.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [current?.agreementId, current?.versionId]);

  if (loading || !current) return <>{children}</>;

  const sanitizedHtml = DOMPurify.sanitize(current.contentHtml ?? '');

  const accept = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const name = current.signatureMethod === 'typed_name' ? typedName.trim() : undefined;
      await portal.acceptAgreement(current.agreementId, current.versionId, name);
      reload();
    } catch (e) {
      // Classify on the structured ApiError.code, not a message regex — a
      // regex match against wording is one copy edit away from silently
      // stopping matching (this is the same bug class classifySaveError was
      // built to fix for ApplicationPage's save flows; see lib/steps.ts).
      const classified = classifySaveError(e);
      if (classified.code === 'agreement_version_stale') {
        toast('This agreement was updated — please review the current version.');
        reload();
      } else {
        setError(classified.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await auth.signOut();
      window.location.assign('/sign-in');
    } catch {
      setBusy(false);
    }
  };

  return (
    <>
      {children}
      <div className="terms-gate-backdrop" aria-hidden />
      <div
        className="terms-gate"
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-gate-title"
        ref={dialogRef}
      >
        <div className="terms-gate-header">
          <h2 id="terms-gate-title">{current.agreementName}</h2>
          <p className="terms-gate-meta">
            Version {current.versionLabel}
            {current.effectiveDate ? ` · Effective ${new Date(current.effectiveDate).toLocaleDateString()}` : ''}
          </p>
        </div>

        <div className="terms-gate-body" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />

        {error && <InlineError message={error} className="terms-gate-error" />}

        {current.signatureMethod === 'typed_name' ? (
          <div className="terms-gate-input-row">
            <label htmlFor="terms-gate-typed-name">
              Type your full legal name to agree to the {current.agreementName}
            </label>
            <input
              id="terms-gate-typed-name"
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Your full name"
              maxLength={200}
            />
          </div>
        ) : (
          <div className="terms-gate-checkbox-row">
            <input
              id="terms-gate-checkbox"
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <label htmlFor="terms-gate-checkbox">I have read and agree to the {current.agreementName}</label>
          </div>
        )}

        <div className="terms-gate-actions">
          {current.allowDecline && (
            <button type="button" className="ghost" onClick={decline} disabled={busy}>
              Decline and Log Out
            </button>
          )}
          <button
            type="button"
            onClick={accept}
            disabled={(current.signatureMethod === 'typed_name' ? typedName.trim().length === 0 : !checked) || busy}
          >
            Accept and Continue
          </button>
        </div>
      </div>
    </>
  );
}
